<script setup>
  import { ref, computed, watch } from "vue";
  import { useUserStore } from "@/stores/useUserStore";
  import DeleteConfirmModal from "@/components/Generics/DeleteConfirmModal.vue";
  
  const props = defineProps({
    user: { type: Object, required: true },
  });
  
  const emit = defineEmits(["close", "refresh"]);
  const store = useUserStore();
  
  const isDeleting = ref(false);
  
  // ✅ Congela o alvo (ID) e o texto (label) pra não mudar após refresh/seleção/etc.
  const frozenUserId = ref(null);
  const frozenUserLabel = ref("");
  
  function getUserId() {
    return props.user?.userId ?? props.user?.id ?? null;
  }
  
  function getUserLabel() {
    const name = props.user?.userName || props.user?.name || "";
    const email = props.user?.email || "";
    const label = (name?.trim?.() || "") || (email?.trim?.() || "");
    const id = getUserId();
    return label || (id ? `#${id}` : "");
  }
  
  function freezeSnapshot() {
    frozenUserId.value = getUserId();
    frozenUserLabel.value = getUserLabel();
  }
  
  // ✅ Mantém o snapshot atualizado ENQUANTO NÃO está deletando
  watch(
    () => [props.user?.userId, props.user?.id, props.user?.userName, props.user?.name, props.user?.email],
    () => {
      if (!isDeleting.value) freezeSnapshot();
    },
    { immediate: true }
  );
  
  const modalTitle = "Apagar Usuário";
  
  const messageTitle = computed(() => {
    const label = frozenUserLabel.value;
    return label
      ? `Você está prestes a apagar o usuário "${label}".`
      : "Você está prestes a apagar este usuário.";
  });
  
  const messageSubtitle = computed(() => "Esta ação não pode ser desfeita.");
  
  function buildErrorMessage(error) {
    return (
      error?.response?.data?.message ||
      error?.message ||
      "Ocorreu um erro ao excluir o usuário."
    );
  }
  
  async function onConfirmDelete() {
    // ✅ congela ANTES de deletar (texto + alvo)
    freezeSnapshot();
    isDeleting.value = true;
  
    try {
      if (!frozenUserId.value) {
        throw new Error("ID do usuário inválido para exclusão.");
      }
  
      // ✅ ajuste o nome do método conforme seu store (mais comum: deleteUserById)
      await store.deleteUserById(frozenUserId.value);
    } catch (error) {
      isDeleting.value = false; // permite tentar novamente
      throw new Error(buildErrorMessage(error)); // toast vem do DeleteConfirmModal
    }
  }
  
  function handleSuccess() {
    emit("refresh");
    emit("close");
  }
  </script>
  
  <template>
    <DeleteConfirmModal
      :show="true"
      :title="modalTitle"
      :messageTitle="messageTitle"
      :messageSubtitle="messageSubtitle"
      confirmText="Sim, apagar"
      cancelText="Cancelar"
      loadingText="Apagando..."
      :onConfirm="onConfirmDelete"
      @success="handleSuccess"
      @close="!isDeleting && emit('close')"
    />
  </template>
  